import { useForm, useStore } from '@tanstack/react-form';
import { defineMessages, useIntl } from 'react-intl';

import type { SegmentedControlLabeledIconSegment } from '~/packages/eds/src/components/SegmentedControl/types';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useFormFocusManager } from '~/hooks/useFormFocusManager';
import { Layout } from '~/packages/eds/src/components/Layout/Layout';
import { Modal } from '~/packages/eds/src/components/Modal/Modal';
import { SegmentedControl } from '~/packages/eds/src/components/SegmentedControl/SegmentedControl';
import { Text } from '~/packages/eds/src/components/Text/Text';

import { useSidebarNavPreference } from './SidebarNavPreferenceContext';
import {
  type SidebarNavOptOutReason,
  submitSidebarNavOptOutFeedback,
} from './submitSidebarNavOptOutFeedback';

const messages = defineMessages({
  title: {
    id: 'SidebarNavOptOut.title',
    defaultMessage: 'Switch back to classic navigation?',
    description: 'Title for the sidebar navigation opt-out feedback modal.',
  },
  description: {
    id: 'SidebarNavOptOut.description',
    defaultMessage:
      'Help us improve the preview by telling us why you are switching back.',
    description: 'Description shown above the opt-out feedback form.',
  },
  reasonLabel: {
    id: 'SidebarNavOptOut.reasonLabel',
    defaultMessage: 'What could we improve?',
    description: 'Label for the sidebar navigation opt-out feedback field.',
  },
  reasonPlaceholder: {
    id: 'SidebarNavOptOut.reasonPlaceholder',
    defaultMessage: 'Tell us what was not working for you',
    description:
      'Placeholder for the sidebar navigation opt-out feedback field.',
  },
  reasonRequired: {
    id: 'SidebarNavOptOut.reasonRequired',
    defaultMessage: 'Please share a brief reason before switching back.',
    description:
      'Validation message when the sidebar navigation opt-out feedback is empty.',
  },
  categoryLabel: {
    id: 'SidebarNavOptOut.categoryLabel',
    defaultMessage: 'Main Reasons',
    description:
      'Label for the optional opt-out reason picker, where any number of reasons can be chosen.',
  },
  categoryNavigation: {
    id: 'SidebarNavOptOut.categoryNavigation',
    defaultMessage: 'Hard to Navigate',
    description: 'Opt-out reason for not being able to find things.',
  },
  categoryBroken: {
    id: 'SidebarNavOptOut.categoryBroken',
    defaultMessage: 'Something Broken',
    description: 'Opt-out reason for the new navigation not working properly.',
  },
  categoryHabit: {
    id: 'SidebarNavOptOut.categoryHabit',
    defaultMessage: 'Prefer Classic',
    description: 'Opt-out reason for preferring the classic navigation.',
  },
  categoryOther: {
    id: 'SidebarNavOptOut.categoryOther',
    defaultMessage: 'Other',
    description: 'Opt-out reason for anything else.',
  },
  note: {
    id: 'SidebarNavOptOut.note',
    defaultMessage: 'Your feedback shapes what we fix next.',
    description:
      'Reassurance shown at the end of the sidebar navigation opt-out form.',
  },
  cancel: {
    id: 'SidebarNavOptOut.cancel',
    defaultMessage: 'Stay in Preview',
    description:
      'Action that dismisses the opt-out modal and keeps the new navigation.',
  },
  submit: {
    id: 'SidebarNavOptOut.submit',
    defaultMessage: 'Switch Back',
    description:
      'Primary action that submits opt-out feedback and returns to classic navigation.',
  },
});

interface SidebarNavOptOutModalProps {
  isVisible: boolean;
  onHide: () => void;
}

export function SidebarNavOptOutModal({
  isVisible,
  onHide,
}: SidebarNavOptOutModalProps) {
  const intl = useIntl();
  const user = useCurrentUser();
  const { optOut } = useSidebarNavPreference();

  const reasonSegments: SegmentedControlLabeledIconSegment[] = [
    {
      icon: { key: 'search' },
      label: intl.formatMessage(messages.categoryNavigation),
      segmentKey: 'navigation',
      text: intl.formatMessage(messages.categoryNavigation),
    },
    {
      icon: { key: 'warning-exclamation' },
      label: intl.formatMessage(messages.categoryBroken),
      segmentKey: 'broken',
      text: intl.formatMessage(messages.categoryBroken),
    },
    {
      icon: { key: 'history-activity-log' },
      label: intl.formatMessage(messages.categoryHabit),
      segmentKey: 'habit',
      text: intl.formatMessage(messages.categoryHabit),
    },
    {
      icon: { key: 'more' },
      label: intl.formatMessage(messages.categoryOther),
      segmentKey: 'other',
      text: intl.formatMessage(messages.categoryOther),
    },
  ];

  const form = useForm({
    defaultValues: {
      reason: '',
      categories: [] as SidebarNavOptOutReason[],
    },
    onSubmit: async ({ value }) => {
      submitSidebarNavOptOutFeedback({
        message: value.reason.trim(),
        name: `${user.first_name} ${user.last_name}`.trim() || undefined,
        email: user.email || undefined,
        reasons: value.categories.length ? value.categories : undefined,
      });
      optOut();
      form.reset();
      onHide();
    },
  });

  useFormFocusManager(form);

  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const submissionAttempts = useStore(
    form.store,
    (state) => state.submissionAttempts,
  );

  const cancelLabel = intl.formatMessage(messages.cancel);
  const submitLabel = intl.formatMessage(messages.submit);

  return (
    <Modal
      isVisible={isVisible}
      header={{
        title: intl.formatMessage(messages.title),
      }}
      footer={{
        actions: [
          {
            label: cancelLabel,
            text: cancelLabel,
            variant: 'tertiary',
            onClick: onHide,
          },
          {
            disabled: isSubmitting,
            icon: { key: 'arrow-left', position: 'left' },
            isLoading: isSubmitting,
            label: submitLabel,
            text: submitLabel,
            variant: 'primary',
            onClick: () => {
              form.handleSubmit().catch(() => {});
            },
          },
        ],
      }}
      onHide={onHide}
    >
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          form.handleSubmit().catch(() => {});
        }}
      >
        <Layout direction="column" spacing="l">
          {/* Body copy rather than the header's `subtitle`, which EDS truncates
              to a single line. */}
          <Text as="p" tone="subdued" variant="body-m">
            {intl.formatMessage(messages.description)}
          </Text>
          <form.Field
            name="reason"
            validators={{
              onSubmit: ({ value }) =>
                value.trim()
                  ? undefined
                  : intl.formatMessage(messages.reasonRequired),
              onChange: ({ value }) =>
                value.trim()
                  ? undefined
                  : intl.formatMessage(messages.reasonRequired),
            }}
          >
            {(field) => (
              <FormField<string, false>
                input={TextArea}
                label={intl.formatMessage(messages.reasonLabel)}
                required
                name={field.name}
                placeholder={intl.formatMessage(messages.reasonPlaceholder)}
                value={field.state.value}
                error={
                  submissionAttempts > 0
                    ? (field.state.meta.errors[0] ?? undefined)
                    : undefined
                }
                inputProps={{ enableAutoResize: true, rows: 4 }}
                onChange={(value) => field.handleChange(value ?? '')}
              />
            )}
          </form.Field>
          <form.Field name="categories">
            {(field) => (
              <Layout direction="column" spacing="s">
                <Text as="p" variant="body-semi-s">
                  {intl.formatMessage(messages.categoryLabel)}
                </Text>
                <SegmentedControl
                  fullWidth
                  label={intl.formatMessage(messages.categoryLabel)}
                  segments={reasonSegments}
                  selectionMode="multiple"
                  value={field.state.value}
                  onChange={(segmentKeys) =>
                    field.handleChange(segmentKeys as SidebarNavOptOutReason[])
                  }
                />
              </Layout>
            )}
          </form.Field>
          <Text as="p" tone="subdued" variant="body-s">
            {intl.formatMessage(messages.note)}
          </Text>
        </Layout>
      </form>
    </Modal>
  );
}
